import { useField } from 'formik';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { Input } from '@treams/treams-ui';
import Select from 'packages/kakapo-ui-kit/components/forms/controls/Select';

function DashboardDateComponent(props: { count: number; unit: string; format: string }) {
	const { t } = useTranslation();
	const [{ value: startDate }, , { setValue: setStartDate }] = useField('startDate');
	const [, , { setValue: setEndDate }] = useField('endDate');

	const value = moment()
		.endOf('day')
		.subtract(1, props.unit as any);

	const modes = [
		{
			label: t(`dashboard.sidebar.filters.timeframe.past.${props.unit}`),
			value,
		},
	];

	for (let i = 0; i < props.count; i += 1) {
		const value = moment()
			.startOf(props.unit as any)
			.subtract(i, props.unit as any);

		modes.push({
			label: value.format(props.format),
			value,
		});
	}

	return (
		<Select
			isSearchable={false}
			unspaced
			small
			value={modes.find(({ value }) => value.isSame(startDate))}
			options={modes}
			onChange={(option) => {
				setStartDate(option!.value.toDate());
				setEndDate(
					option!.value
						.clone()
						.add(1, props.unit as any)
						.toDate(),
				);
			}}
		/>
	);
}

export default function DashboardDateSelector() {
	const { t } = useTranslation();

	const [mode, setMode] = useState<'month' | 'quarter' | 'year' | 'custom'>('month');
	const [{ value: startDate }, , { setValue: setStartDate }] = useField('startDate');
	const [{ value: endDate }, , { setValue: setEndDate }] = useField('endDate');

	const modes: { label: string; value: 'month' | 'quarter' | 'year' | 'custom' }[] = [
		{
			label: t('dashboard.sidebar.filters.timeframe.mode.month'),
			value: 'month',
		},
		{
			label: t('dashboard.sidebar.filters.timeframe.mode.quarter'),
			value: 'quarter',
		},
		{
			label: t('dashboard.sidebar.filters.timeframe.mode.year'),
			value: 'year',
		},
		{
			label: t('dashboard.sidebar.filters.timeframe.mode.custom'),
			value: 'custom',
		},
	];

	return (
		<Flex>
			<Box flexShrink={1} flexGrow={1} mr={2} flexBasis={1}>
				<Select
					isSearchable={false}
					unspaced
					small
					value={modes.find(({ value }) => value === mode)}
					options={modes}
					onChange={(option) => {
						setMode(option!.value);

						const startDate = moment()
							.endOf('day')
							.subtract(1, option!.value as any);

						const endDate = startDate.clone().add(1, option!.value as any);

						setStartDate(startDate);
						setEndDate(endDate);
					}}
				/>
			</Box>
			{mode === 'month' && (
				<Box flexShrink={1} flexGrow={1} flexBasis={70}>
					<DashboardDateComponent count={24} unit="month" format="MMMM YYYY" />
				</Box>
			)}
			{mode === 'quarter' && (
				<Box flexShrink={1} flexGrow={1} flexBasis={70}>
					<DashboardDateComponent count={8} unit="quarter" format="[Q]Q YYYY" />
				</Box>
			)}
			{mode === 'year' && (
				<Box flexShrink={1} flexGrow={1} flexBasis={70}>
					<DashboardDateComponent count={4} unit="year" format="YYYY" />
				</Box>
			)}
			{mode === 'custom' && (
				<Box mb={2} flexGrow={0} flexShrink={0}>
					<Box mb={2}>
						<Input.DatePicker
							value={startDate}
							placeholder={t('dashboard.sidebar.filters.timeframe.fromDatePlaceholder')}
							onChange={(date) => date && setStartDate(date)}
						/>
					</Box>
					<Input.DatePicker
						value={endDate}
						placeholder={t('dashboard.sidebar.filters.timeframe.toDatePlaceholder')}
						onChange={(date) => date && setEndDate(date)}
					/>
				</Box>
			)}
		</Flex>
	);
}
